// src/host/HostListings.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../lib/useAuth";
import { watchHostListings, db } from "../firebase";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";

export default function HostListings() {
  const { user, loading } = useAuth();
  const loc = useLocation();
  const justSubmitted = new URLSearchParams(loc.search).get("submitted") === "1";

  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");
  const [busyId, setBusyId] = useState("");

  useEffect(() => {
    if (!user) return;
    const unsub = watchHostListings({
      ownerUid: user.uid,
      onChange: setItems,
      onError: (e) => setErr(e?.message || "Failed to load listings."),
    });
    return () => unsub && unsub();
  }, [user]);

  const itemsSorted = useMemo(() => {
    // Keep existing behavior simple; just provide stable ordering for UX.
    return [...(items || [])].sort((a, b) => {
      const ad =
        a?.updatedAt?.toDate?.()?.getTime?.() ||
        a?.submittedAt?.toDate?.()?.getTime?.() ||
        a?.createdAt?.toDate?.()?.getTime?.() ||
        0;
      const bd =
        b?.updatedAt?.toDate?.()?.getTime?.() ||
        b?.submittedAt?.toDate?.()?.getTime?.() ||
        b?.createdAt?.toDate?.()?.getTime?.() ||
        0;
      return bd - ad;
    });
  }, [items]);

  async function togglePause(listing) {
    if (!user || !listing?.id) return;

    setErr("");
    const current = String(listing.status || "").toLowerCase();

    // Only allow pause/unpause on published/paused to keep MVP safe.
    const canPause = current === "published";
    const canUnpause = current === "paused";

    if (!canPause && !canUnpause) {
      setErr("Only published listings can be paused for now.");
      return;
    }

    const nextStatus = canPause ? "paused" : "published";
    const verb = canPause ? "pause/unpublish" : "republish";

    const ok =
      typeof window !== "undefined"
        ? window.confirm(
            `Are you sure you want to ${verb} "${listing.title || "this listing"}"?`
          )
        : true;

    if (!ok) return;

    setBusyId(listing.id);
    try {
      const ref_ = doc(db, "listings", listing.id);

      await updateDoc(ref_, {
        status: nextStatus,
        updatedAt: serverTimestamp(),
        // Optional audit-ish hint for you later:
        pausedAt: nextStatus === "paused" ? serverTimestamp() : null,
        publishedAt:
          nextStatus === "published"
            ? listing?.publishedAt || serverTimestamp()
            : listing?.publishedAt || null,
      });
    } catch (e) {
      console.error(e);
      setErr("Couldn't update listing status. Please try again.");
    } finally {
      setBusyId("");
    }
  }

  if (loading) return null;

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Your listings</h1>
        <Link
          to="/host/listings/new"
          className="rounded-full bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-[#B8860B]"
        >
          New listing
        </Link>
      </div>

      <p className="mt-2 text-neutral-600">Create and manage day-use listings.</p>

      {justSubmitted && (
        <div className="mt-4 rounded-xl border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
          Your listing was submitted and is now <b>pending review</b>. We'll
          notify you when it's published.
        </div>
      )}

      {err && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {err}
        </div>
      )}

      <div className="mt-6 rounded-2xl border border-neutral-200 p-6">
        {itemsSorted.length === 0 ? (
          <div className="text-neutral-700">
            No listings yet.
            <div className="mt-1 text-sm text-neutral-500">
              After you publish, it will appear here with status <b>pending</b>{" "}
              until approved.
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-neutral-500">
                <tr>
                  <Th>Title</Th>
                  <Th>City</Th>
                  <Th>Price</Th>
                  <Th>Status</Th>
                  <Th>Submitted</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {itemsSorted.map((l) => {
                  const status = String(l.status || "").toLowerCase();
                  const isBusy = busyId === l.id;

                  const showPauseButton =
                    status === "published" || status === "paused";

                  return (
                    <tr key={l.id} className="border-t">
                      <Td className="font-medium">{l.title || "—"}</Td>
                      <Td>
                        {[l.city, l.stateCode].filter(Boolean).join(", ") || "—"}
                      </Td>
                      <Td>
                        {isFiniteNum(l.dayPrice) ? fmtCurrency(l.dayPrice) : "—"}
                      </Td>
                      <Td>{statusBadge(l.status)}</Td>
                      <Td>{fmtDate(l.submittedAt)}</Td>
                      <Td>
                        <div className="flex flex-wrap items-center gap-2">
                          <Link
                            to={`/host/listings/edit/${l.id}`}
                            className="rounded-full border border-neutral-300 px-3 py-1.5 text-xs hover:border-neutral-400"
                          >
                            Edit
                          </Link>

                          {showPauseButton && (
                            <button
                              type="button"
                              onClick={() => togglePause(l)}
                              disabled={isBusy}
                              className={[
                                "rounded-full px-3 py-1.5 text-xs font-semibold",
                                "border",
                                status === "published"
                                  ? "border-neutral-300 hover:border-neutral-400"
                                  : "border-black bg-black text-white hover:opacity-90",
                                isBusy ? "opacity-60" : "",
                              ].join(" ")}
                              title={
                                status === "published"
                                  ? "Pause/unpublish this listing"
                                  : "Republish this listing"
                              }
                            >
                              {isBusy
                                ? "Updating…"
                                : status === "published"
                                ? "Pause"
                                : "Republish"}
                            </button>
                          )}
                        </div>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="mt-3 text-[11px] text-neutral-500">
              Tip: Pausing a listing hides it from discovery without deleting your
              details.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Th({ children }) {
  return <th className="py-2 pr-4">{children}</th>;
}

function Td({ children, className = "" }) {
  return <td className={`py-2 pr-4 ${className}`}>{children}</td>;
}

function statusBadge(s) {
  const v = String(s || "").toLowerCase();

  const cls =
    v === "published"
      ? "bg-green-100 text-green-700"
      : v === "pending"
      ? "bg-amber-100 text-amber-800"
      : v === "rejected"
      ? "bg-red-100 text-red-700"
      : v === "paused"
      ? "bg-neutral-200 text-neutral-800"
      : "bg-neutral-100 text-neutral-700";

  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs ${cls}`}>
      {s || "—"}
    </span>
  );
}

function isFiniteNum(n) {
  return typeof n === "number" && Number.isFinite(n);
}

function fmtCurrency(n) {
  try {
    return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
  } catch {
    return `$${n}`;
  }
}

function fmtDate(ts) {
  try {
    if (!ts) return "—";
    const d =
      typeof ts?.toDate === "function"
        ? ts.toDate()
        : typeof ts === "number"
        ? new Date(ts)
        : new Date(ts);
    return d.toLocaleString();
  } catch {
    return "—";
  }
}
